import { KeyTokenName, ROUTES, appVersion } from "@/constants";
import { getUrlServer } from "@/helpers";
import { ERole, IApplicationState } from "@/store-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import Person2Icon from "@mui/icons-material/Person2";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { Avatar } from "@mui/material";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ForumIcon from "@mui/icons-material/Forum";
import MicIcon from "@mui/icons-material/Mic";
import { alpha, styled, useTheme } from "@mui/material/styles";
import clsx from "clsx";
import * as React from "react";
import { Fragment, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import { AllTypeActions } from "@/store-redux";
import ClassIcon from "@mui/icons-material/Class";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import QuizIcon from "@mui/icons-material/Quiz";
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import { isPremiumUser } from "@/helpers";
import Chip from "@mui/material/Chip";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Button from "@mui/material/Button";
import BrandLogo from "@/components/BrandLogo";
import { NotificationBell } from "@/components/NotificationBell";

const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop: string) => prop !== "open" && prop !== "isMobile" })<{
  open?: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }: { theme: any; open?: boolean; isMobile?: boolean }) => ({
  flexGrow: 1,
  padding: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${drawerWidth}px`,
  ...(open &&
    !isMobile && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
}));

interface IPersistentDrawerLeftProps {
  children: JSX.Element;
}

export default function PersistentDrawerLeft({ children }: IPersistentDrawerLeftProps) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(!isMobile);
  const [hasClosedForCurrentPage, setHasClosedForCurrentPage] = React.useState(false);
  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({
    Speaking: true,
  });
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const currentUser = useSelector((state: IApplicationState) => state.currentUser);
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isSidebarOpen = useSelector((state: IApplicationState) => state.isSidebarOpen);
  const isDark = appMode === "dark";
  const role = currentUser?.data?.role;
  const isPremium = isPremiumUser(currentUser?.data);

  interface INavItem {
    title: string;
    icon: React.ReactNode;
    path?: string;
    isPremiumOnly?: boolean;
    children?: {
      title: string;
      icon: React.ReactNode;
      path: string;
      isPremiumOnly?: boolean;
    }[];
  }

  const ListItemNav: INavItem[] =
    role === ERole.TEACHER
      ? [
          {
            title: "Dashboard",
            icon: <DashboardIcon />,
            path: ROUTES.TEACHER_DASHBOARD,
          },
          {
            title: "Class",
            icon: <ClassIcon />,
            path: ROUTES.TEACHER_CLASS,
          },
        ]
      : role === ERole.USER
        ? [
            {
              title: "Speaking",
              icon: <ForumIcon />,
              children: [
                {
                  title: "Phát triển ý tưởng",
                  icon: <LocalLibraryIcon />,
                  path: ROUTES.LEARN,
                },
                {
                  title: "Ứng dụng ngữ pháp",
                  icon: <MenuBookIcon />,
                  path: ROUTES.LEARN_GRAMMAR,
                },
                {
                  title: "Nâng cấp từ vựng",
                  icon: <SpellcheckIcon />,
                  path: ROUTES.WORDBANK,
                },
                {
                  title: "Luyện tập phát âm",
                  icon: <MicIcon />,
                  path: ROUTES.ASSESSMENT_PRONUNCIATION,
                },
                {
                  title: "Kiểm tra",
                  icon: <AssignmentIcon />,
                  path: ROUTES.ASSESSMENT_QUESTION,
                },
              ],
            },
            {
              title: "Reading",
              icon: <MenuBookIcon />,
              isPremiumOnly: true,
              children: [
                {
                  title: "Đọc luyện đề",
                  icon: <MenuBookIcon />,
                  path: ROUTES.IELTS_EXAMS_READING,
                },
                {
                  title: "Đọc dạng bài",
                  icon: <QuizIcon />,
                  path: ROUTES.READING_PRACTICE,
                },
              ],
            },
            {
              title: "Listening",
              icon: <LibraryMusicIcon />,
              isPremiumOnly: true,
              children: [
                {
                  title: "Luyện đề",
                  icon: <LibraryMusicIcon />,
                  path: ROUTES.IELTS_EXAMS_LISTENING,
                },
                // {
                //   title: "Basic IELTS Listening",
                //   icon: <MenuBookIcon />,
                //   path: ROUTES.IELTS_EXAMS_BASIC_LISTENING,
                // },
                {
                  title: "Luyện tập nghe",
                  icon: <QuizIcon />,
                  path: ROUTES.LISTENING_PRACTICE,
                },
              ],
            },
            {
              title: "Writing",
              icon: <EditIcon />,
              path: ROUTES.IELTS_EXAMS_WRITING,
              isPremiumOnly: true,
            },
          ]
        : [];

  const secondaryNavItems: INavItem[] =
    role === ERole.USER
      ? [
          {
            title: "Lớp học của bạn",
            icon: <ClassIcon />,
            path: ROUTES.STUDENT_MY_CLASS,
          },
          {
            title: "Bài tập về nhà",
            icon: <AssignmentTurnedInIcon />,
            path: ROUTES.STUDENT_ASSIGNMENTS,
          },
          {
            title: "Bài đã học",
            icon: <Person2Icon />,
            path: ROUTES.PROFILE,
          },
          {
            title: "Thông tin tài khoản",
            icon: <ManageAccountsIcon />,
            path: ROUTES.ACCOUNT,
          },
        ]
      : [];

  const isItemActive = useCallback(
    (item: any): boolean => {
      if (!item.path) return false;

      if (item.path.includes("?")) {
        const [pathPart, queryPart] = item.path.split("?");
        return pathname === pathPart && search.includes(queryPart);
      }

      if (item.path === ROUTES.LEARN && pathname.includes("learn") && !pathname.includes("grammar")) {
        return true;
      }
      if (item.path === ROUTES.LEARN_GRAMMAR && pathname.includes("grammar")) {
        return true;
      }
      if (
        item.path === ROUTES.WORDBANK &&
        (pathname.includes("vocabulary") || pathname.includes("wordbook") || pathname.includes("wordbank"))
      ) {
        return true;
      }
      if (
        item.path === ROUTES.ASSESSMENT_PRONUNCIATION &&
        (pathname.includes("assessment/pronunciation") || search.includes("mode=pronunciation"))
      ) {
        return true;
      }
      if (
        item.path === ROUTES.ASSESSMENT_QUESTION &&
        (pathname.includes("assessment/question") ||
          pathname.includes("speaking/reaction-test") ||
          (pathname === "/assessment" && !search.includes("mode=pronunciation")))
      ) {
        return true;
      }
      if (
        item.path === ROUTES.IELTS_EXAMS_READING &&
        pathname.includes("ielts-exams/reading") &&
        !pathname.includes("ielts-exams/reading/practice")
      ) {
        return true;
      }
      if (item.path === ROUTES.READING_PRACTICE && pathname.includes("ielts-exams/reading/practice")) {
        return true;
      }
      if (
        item.path === ROUTES.IELTS_EXAMS_LISTENING &&
        (pathname === ROUTES.IELTS_EXAMS_LISTENING || pathname === ROUTES.IELTS_EXAMS) &&
        !pathname.includes("ielts-exams/listening/practice") &&
        !pathname.includes("ielts-exams/basic-listening")
      ) {
        return true;
      }
      if (item.path === ROUTES.IELTS_EXAMS_BASIC_LISTENING && pathname.includes("ielts-exams/basic-listening")) {
        return true;
      }
      if (item.path === ROUTES.LISTENING_PRACTICE && pathname.includes("ielts-exams/listening/practice")) {
        return true;
      }
      if (item.path === ROUTES.IELTS_EXAMS_WRITING && pathname.includes("ielts-exams/writing")) {
        return true;
      }
      if (
        item.path === ROUTES.STUDENT_MY_CLASS &&
        (pathname.includes("student/my-class") || pathname.includes("student/class"))
      ) {
        return true;
      }
      if (item.path === ROUTES.STUDENT_ASSIGNMENTS && pathname.includes("student/assignment")) {
        return true;
      }
      if (item.path === ROUTES.PROFILE && pathname === ROUTES.PROFILE) {
        return true;
      }
      if (item.path === ROUTES.ACCOUNT && pathname.includes("account")) {
        return true;
      }
      if (item.path === ROUTES.TEACHER_DASHBOARD && pathname.includes("teacher/dashboard")) {
        return true;
      }
      if (item.path === ROUTES.TEACHER_CLASS && pathname.includes("teacher/class")) {
        return true;
      }

      return pathname === item.path;
    },
    [pathname, search]
  );

  const handleItemClick = (item: any) => {
    if (item.children) {
      setOpenMenus((prev) => ({
        ...prev,
        [item.title]: !prev[item.title],
      }));
    } else {
      navigate(item.path);
      if (isMobile) {
        handleDrawerClose();
      }
    }
  };

  useEffect(() => {
    if (role === ERole.USER) {
      const isSpeakingActive =
        pathname.includes("grammar") ||
        pathname.includes("learn") ||
        pathname.includes("vocabulary") ||
        pathname.includes("wordbook") ||
        pathname.includes("assessment");

      if (isSpeakingActive) {
        setOpenMenus((prev) => ({ ...prev, Speaking: true }));
      }

      if (pathname.includes("ielts-exams/reading")) {
        setOpenMenus((prev) => ({ ...prev, Reading: true }));
      }

      if (pathname.includes("ielts-exams/listening") || pathname.includes("ielts-exams/basic-listening")) {
        setOpenMenus((prev) => ({ ...prev, Listening: true }));
      }
    }
  }, [pathname, role]);

  const handleDrawerOpen = React.useCallback(() => {
    setOpen(true);
    dispatch({ type: AllTypeActions.SET_SIDEBAR_OPEN, payload: true });
  }, [dispatch]);

  const handleDrawerClose = React.useCallback(() => {
    setOpen(false);
    dispatch({ type: AllTypeActions.SET_SIDEBAR_OPEN, payload: false });
  }, [dispatch]);

  const handleLogout = () => {
    Swal.fire({
      title: "Bạn có chắc chắn muốn đăng xuất?",
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: "Hủy",
      confirmButtonText: "Đăng xuất",
      background: isDark ? "#1e293b" : "#fff",
      color: isDark ? "#fff" : "#0f172a",
      confirmButtonColor: "#EF4444",
      cancelButtonColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem(KeyTokenName);
        window.location.href = "/login";
      }
    });
  };

  const toggleTheme = () => {
    dispatch({ type: AllTypeActions.SET_APP_MODE, payload: isDark ? "light" : "dark" });
  };

  useEffect(() => {
    // Reset the flag when pathname changes
    setHasClosedForCurrentPage(false);
  }, [pathname]);

  useEffect(() => {
    dispatch({ type: AllTypeActions.SET_SIDEBAR_OPEN, payload: open });
  }, [dispatch, open]); // Sync state whenever it changes

  useEffect(() => {
    if (isSidebarOpen !== undefined && isSidebarOpen !== open) {
      setOpen(isSidebarOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebarOpen]);

  useEffect(() => {
    if (
      (pathname.indexOf("answer-question") !== -1 || pathname.indexOf("/learn/by-question/") !== -1) &&
      open &&
      !hasClosedForCurrentPage
    ) {
      handleDrawerClose();
      setHasClosedForCurrentPage(true);
    }
  }, [pathname, open, hasClosedForCurrentPage, handleDrawerClose]);

  return (
    <Box sx={{ display: "flex" }}>
      {!open && (
        <Box
          sx={{
            position: "fixed",
            zIndex: 1101,
            left: 0,
            top: 0,
            p: 1.5,
          }}
        >
          <IconButton
            onClick={handleDrawerOpen}
            sx={{
              bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)",
              backdropFilter: "blur(10px)",
              border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(15, 23, 42, 0.1)"}`,
              color: isDark ? "#fff" : "#0f172a",
              "&:hover": {
                bgcolor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(15, 23, 42, 0.1)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: "none",
            bgcolor: isDark ? "rgba(15, 23, 42, 0.8)" : "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(20px)",
            borderRight: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)"}`,
            transition: "all 0.3s ease",
          },
          "& .MuiPaper-root": {
            color: isDark ? "#fff" : "#0f172a",
            "& .MuiListItemIcon-root": {
              minWidth: "32px",
            },
            "& .MuiListItem-root": {
              color: isDark ? "rgba(255, 255, 255, 0.6)" : "#64748B",
              borderRadius: "12px",
              margin: "4px 8px",
              padding: "0",
              transition: "all 0.2s ease-in-out",
              "& .MuiListItemButton-root": {
                padding: "10px 12px",
                borderRadius: "12px",
                minHeight: "44px",
              },
              "& span": {
                fontSize: "14px",
                fontWeight: "600",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              },
              "& svg": {
                fontSize: "20px",
                color: isDark ? "rgba(255, 255, 255, 0.5)" : "#64748B",
                transition: "color 0.2s ease",
              },
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)",
                color: "#6366F1",
                "& svg": {
                  color: "#6366F1",
                },
              },
              "&.active": {
                color: "#6366F1",
                backgroundColor: alpha("#6366F1", isDark ? 0.15 : 0.08),
                "& svg": {
                  color: "#6366F1",
                },
                "& span": {
                  fontWeight: "700",
                },
              },
              "&.active-parent": {
                color: "#6366F1",
                "& svg": {
                  color: "#6366F1",
                },
                "& span": {
                  fontWeight: "700",
                },
              },
            },
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        {/* Header with logo and close button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "20px 16px",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <BrandLogo isDark={isDark} />
          </Box>
          <IconButton
            onClick={handleDrawerClose}
            size="small"
            sx={{
              color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(15, 23, 42, 0.5)",
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)",
              },
            }}
          >
            {theme.direction === "ltr" ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* User Profile Section */}
        <Box
          onClick={() => navigate(ROUTES.ACCOUNT)}
          sx={{
            padding: "12px 12px 10px 12px",
            borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)"}`,
            borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)"}`,
            cursor: "pointer",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <Avatar
              sx={{
                height: "44px",
                width: "44px",
                border: `2px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(15, 23, 42, 0.1)"}`,
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#E5E7EB",
              }}
              src={
                currentUser.data?.avatarPath ? `${getUrlServer(currentUser.data?.avatarPath)}` : "/images/people.png"
              }
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "2px",
                }}
              >
                <Typography
                  sx={{
                    color: isDark ? "#fff" : "#1F2937",
                    fontWeight: "800",
                    fontSize: "15px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {currentUser.data?.username}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {isPremium && (
                    <Chip
                      label="PRO"
                      size="small"
                      sx={{
                        height: "18px",
                        fontSize: "10px",
                        fontWeight: "800",
                        backgroundColor: "#FFC107",
                        color: "#000",
                        borderRadius: "1000px",
                        "& .MuiChip-label": { px: "6px" },
                      }}
                    />
                  )}
                  <NotificationBell />
                </Box>
              </Box>
              <Typography
                sx={{
                  color: isDark ? "rgba(255, 255, 255, 0.4)" : "#6B7280",
                  fontSize: "12px",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {currentUser.data?.email}
              </Typography>
            </Box>
          </Box>

          {/* Mini Token Usage Progress Bar / Premium Upgrade Banner */}
          {role === ERole.USER &&
            (() => {
              const isPremium = isPremiumUser(currentUser.data);
              const hasGroup = !!currentUser.data?.group;
              const toppedUpBalance = currentUser.data?.toppedUpBalance ?? 0;

              // Non-Premium (and not in group) User: Show compact Free quota status
              if (!isPremium && !hasGroup) {
                return (
                  <Box
                    sx={{
                      mt: 1,
                      p: "8px 10px",
                      borderRadius: "10px",
                      bgcolor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.025)",
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.06)"}`,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AutoAwesomeIcon sx={{ fontSize: 13, color: isDark ? "#9CA3AF" : "#6B7280" }} />
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 700,
                            color: isDark ? "rgba(255, 255, 255, 0.7)" : "#4B5563",
                          }}
                        >
                          Hạn mức AI
                        </Typography>
                      </Box>
                      <Chip
                        label="Free"
                        size="small"
                        sx={{
                          height: "17px",
                          fontSize: "9px",
                          fontWeight: 700,
                          bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                          color: isDark ? "rgba(255,255,255,0.5)" : "#6B7280",
                          borderRadius: "100px",
                          "& .MuiChip-label": { px: "5px" },
                        }}
                      />
                    </Box>

                    {toppedUpBalance > 0 ? (
                      <Box sx={{ mt: 0.6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: "10px", color: isDark ? "rgba(255,255,255,0.5)" : "#6B7280" }}>
                          Số dư ví:
                        </Typography>
                        <Typography sx={{ fontSize: "10.5px", fontWeight: 800, color: "#14B8A6" }}>
                          {toppedUpBalance.toLocaleString("vi-VN")} đ
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          mt: 0.4,
                          fontSize: "9.5px",
                          color: isDark ? "rgba(255, 255, 255, 0.4)" : "#94A3B8",
                          fontStyle: "italic",
                        }}
                      >
                        Chưa kích hoạt gói tháng
                      </Typography>
                    )}
                  </Box>
                );
              }

              const limit =
                currentUser.data?.monthlyQuotaLimit ??
                currentUser.data?.group?.monthlyQuotaLimit ??
                currentUser.data?.currentPackage?.monthlyQuota ??
                130000;
              const used = currentUser.data?.monthlyQuotaUsed ?? 0;
              const remainingQuota = Math.max(0, limit - used);

              const quotaPercent = limit > 0 ? Math.round((remainingQuota / limit) * 100) : 0;
              const toppedUpPercent = limit > 0 ? Math.round((toppedUpBalance / limit) * 100) : 0;

              const isExhausted = remainingQuota <= 0 && toppedUpBalance <= 0;
              const labelColor = isDark ? "rgba(255,255,255,0.5)" : "#6B7280";

              let quotaGradient = "linear-gradient(90deg, #6366F1 0%, #A855F7 100%)";
              let quotaPctColor = "#A855F7";

              if (quotaPercent < 20) {
                quotaGradient = "linear-gradient(90deg, #EF4444 0%, #F87171 100%)";
                quotaPctColor = "#EF4444";
              } else if (quotaPercent < 50) {
                quotaGradient = "linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)";
                quotaPctColor = "#F59E0B";
              }

              let toppedUpGradient = "linear-gradient(90deg, #2DD4BF 0%, #14B8A6 100%)";
              let toppedUpPctColor = "#2DD4BF";

              if (toppedUpPercent < 20) {
                toppedUpGradient = "linear-gradient(90deg, #EF4444 0%, #F87171 100%)";
                toppedUpPctColor = "#EF4444";
              }

              const hasToppedUp = toppedUpBalance > 0 || toppedUpPercent > 0;
              const nextQuotaRefreshDate = currentUser.data?.nextQuotaRefreshDate;
              const isValidRefreshDate = Boolean(nextQuotaRefreshDate && moment(nextQuotaRefreshDate).isValid());
              const resetDateStr = isValidRefreshDate ? moment(nextQuotaRefreshDate).format("DD/MM") : "";

              return (
                <Box
                  sx={{
                    mt: 1,
                    p: "8px 10px",
                    borderRadius: "10px",
                    bgcolor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.025)",
                    border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.06)"}`,
                  }}
                >
                  {/* Card Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AutoAwesomeIcon sx={{ fontSize: 13, color: isDark ? "#A855F7" : "#6366F1" }} />
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 800,
                          color: isDark ? "rgba(255, 255, 255, 0.9)" : "#374151",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Hạn mức AI
                      </Typography>
                    </Box>
                    {isExhausted && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(ROUTES.ACCOUNT);
                        }}
                        startIcon={<RocketLaunchIcon sx={{ fontSize: "11px !important" }} />}
                        sx={{
                          px: "8px",
                          py: "1px",
                          minWidth: "auto",
                          height: "18px",
                          fontSize: "9.5px",
                          fontWeight: 800,
                          color: "#fff",
                          background: "linear-gradient(90deg, #EF4444 0%, #F87171 100%)",
                          borderRadius: "100px",
                          boxShadow: "0 2px 6px rgba(239, 68, 68, 0.35)",
                          textTransform: "none",
                          lineHeight: 1.2,
                          "&:hover": {
                            background: "linear-gradient(90deg, #DC2626 0%, #EF4444 100%)",
                          },
                        }}
                      >
                        Nạp ngay
                      </Button>
                    )}
                  </Box>

                  {/* Quota Rows Container */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                    {/* Hạn mức hàng tháng */}
                    <Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.2 }}>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            fontWeight: 600,
                            color: labelColor,
                          }}
                          title={
                            isValidRefreshDate
                              ? `Làm mới hạn mức vào ngày: ${moment(nextQuotaRefreshDate).format("DD/MM/YYYY")}`
                              : undefined
                          }
                        >
                          Hàng tháng{" "}
                          {resetDateStr && (
                            <span style={{ fontSize: "9px", opacity: 0.9, fontWeight: 700 }}>
                              (Reset {resetDateStr})
                            </span>
                          )}
                        </Typography>
                        {!isExhausted && (
                          <Typography
                            sx={{
                              fontSize: "10px",
                              fontWeight: 800,
                              color: quotaPctColor,
                            }}
                          >
                            {quotaPercent}%
                          </Typography>
                        )}
                      </Box>

                      {/* Track */}
                      <Box
                        sx={{
                          width: "100%",
                          height: "4px",
                          borderRadius: "100px",
                          bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {/* Fill */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            height: "100%",
                            width: `${Math.min(100, quotaPercent)}%`,
                            background: quotaGradient,
                            borderRadius: "100px",
                            transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Hạn mức nạp thêm */}
                    {hasToppedUp && (
                      <Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.2 }}>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              fontWeight: 600,
                              color: labelColor,
                            }}
                          >
                            Nạp thêm
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              fontWeight: 800,
                              color: toppedUpPctColor,
                            }}
                          >
                            {toppedUpPercent}%
                          </Typography>
                        </Box>

                        {/* Track */}
                        <Box
                          sx={{
                            width: "100%",
                            height: "4px",
                            borderRadius: "100px",
                            bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          {/* Fill */}
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              height: "100%",
                              width: `${Math.min(100, toppedUpPercent)}%`,
                              background: toppedUpGradient,
                              borderRadius: "100px",
                              transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })()}
        </Box>

        {/* Navigation List */}
        <List
          sx={{
            padding: "8px 12px",
            flex: 1,
          }}
        >
          {ListItemNav.map((item, index) => {
            const hasChildren = !!item.children;
            const isOpen = openMenus[item.title];
            const isActive = isItemActive(item);
            const isParentActive = hasChildren && item.children?.some((child) => isItemActive(child));

            return (
              <Fragment key={index}>
                <ListItem
                  disablePadding
                  onClick={() => handleItemClick(item)}
                  className={clsx(isActive && "active", isParentActive && "active-parent")}
                  sx={{
                    display: "block",
                  }}
                >
                  <ListItemButton>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.title} />
                    {item.isPremiumOnly && !isPremium && (
                      <LockIcon
                        sx={{ fontSize: 16, color: isDark ? "rgba(255,255,255,0.4)" : "rgba(15,23,42,0.4)", ml: 1 }}
                      />
                    )}
                    {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                  </ListItemButton>
                </ListItem>

                {hasChildren && (
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: "16px",
                          top: 0,
                          bottom: "16px",
                          width: "1.5px",
                          bgcolor: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(15, 23, 42, 0.15)",
                          borderRadius: "1px",
                        },
                      }}
                    >
                      {item.children?.map((child, childIdx) => {
                        const isChildActive = isItemActive(child);
                        return (
                          <ListItem
                            key={childIdx}
                            disablePadding
                            onClick={() => handleItemClick(child)}
                            className={clsx(isChildActive && "active")}
                            sx={{
                              margin: "4px 8px 4px 20px !important",
                              width: "calc(100% - 28px) !important",
                            }}
                          >
                            <ListItemButton
                              sx={{
                                padding: "8px 6px 8px 2px !important",
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: "24px !important",
                                  "& svg": {
                                    fontSize: "18px !important",
                                    opacity: 0.8,
                                  },
                                }}
                              >
                                {child.icon}
                              </ListItemIcon>
                              <ListItemText primary={child.title} />
                              {child.isPremiumOnly && !isPremium && (
                                <LockIcon
                                  sx={{
                                    fontSize: 16,
                                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(15,23,42,0.4)",
                                    ml: 1,
                                  }}
                                />
                              )}
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Fragment>
            );
          })}
        </List>

        {/* Upgrade Card for Free Users */}
        {!isPremium && role === ERole.USER && (
          <Box sx={{ padding: "12px 16px", mt: 1 }}>
            <Box
              sx={{
                background: isDark
                  ? "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)"
                  : "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                borderRadius: "16px",
                padding: "20px 16px",
                color: "white",
                position: "relative",
                overflow: "hidden",
                boxShadow: isDark ? "0 8px 20px rgba(99, 102, 241, 0.3)" : "0 8px 20px rgba(79, 70, 229, 0.2)",
              }}
            >
              <RocketLaunchIcon
                sx={{
                  position: "absolute",
                  right: "-10px",
                  top: "-10px",
                  fontSize: "64px",
                  opacity: 0.15,
                  transform: "rotate(15deg)",
                }}
              />
              <Typography sx={{ fontSize: "14px", fontWeight: "800", mb: 0.5, letterSpacing: "-0.01em" }}>
                Nâng cấp Premium
              </Typography>
              <Typography sx={{ fontSize: "11px", opacity: 0.9, mb: 2, lineHeight: 1.4, fontWeight: 500 }}>
                Mở khóa lộ trình học cá nhân hóa và toàn bộ tính năng cao cấp.
              </Typography>
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={() => navigate(ROUTES.UPGRADE)}
                sx={{
                  backgroundColor: "white",
                  color: isDark ? "#6366F1" : "#4F46E5",
                  fontSize: "12px",
                  fontWeight: "800",
                  textTransform: "none",
                  borderRadius: "10px",
                  py: 1,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#f8f8f8",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                }}
              >
                Nâng cấp ngay
              </Button>
            </Box>
          </Box>
        )}

        {/* Bottom Section */}
        <Box
          sx={{
            borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)"}`,
            padding: "8px 12px",
            marginTop: "auto",
          }}
        >
          {/* Secondary Nav Items (Class, Assignments, Profile) */}
          {secondaryNavItems.length > 0 && (
            <List sx={{ padding: 0, mb: 1 }}>
              {secondaryNavItems.map((item, index) => {
                const isActive = isItemActive(item);
                return (
                  <ListItem
                    key={index}
                    disablePadding
                    onClick={() => handleItemClick(item)}
                    className={clsx(isActive && "active")}
                    sx={{
                      display: "block",
                      margin: "4px 0 !important",
                    }}
                  >
                    <ListItemButton>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.title} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}

          {secondaryNavItems.length > 0 && (
            <Box
              sx={{
                borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)"}`,
                mb: 1,
                mt: 1,
              }}
            />
          )}

          {/* Theme Toggle Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              padding: "10px 12px",
              borderRadius: "12px",
              mb: 0.5,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)",
                "& svg": { color: "#6366F1" },
                "& .theme-text": { color: "#6366F1" },
              },
            }}
            onClick={toggleTheme}
          >
            {isDark ? (
              <LightModeIcon
                sx={{ fontSize: "18px", color: isDark ? "rgba(255, 255, 255, 0.4)" : "#64748B", marginRight: "10px" }}
              />
            ) : (
              <DarkModeIcon
                sx={{ fontSize: "18px", color: isDark ? "rgba(255, 255, 255, 0.4)" : "#64748B", marginRight: "10px" }}
              />
            )}
            <Typography
              className="theme-text"
              sx={{
                color: isDark ? "rgba(255, 255, 255, 0.6)" : "#64748B",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              {isDark ? "Giao diện sáng" : "Giao diện tối"}
            </Typography>
          </Box>

          {/* Logout Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              padding: "10px 12px",
              borderRadius: "12px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: isDark ? "rgba(244, 63, 94, 0.1)" : "#FFF1F2",
                "& svg": { color: "#F43F5E" },
                "& .logout-text": { color: "#F43F5E" },
              },
            }}
            onClick={handleLogout}
          >
            <LogoutIcon
              sx={{ fontSize: "18px", color: isDark ? "rgba(255, 255, 255, 0.4)" : "#64748B", marginRight: "10px" }}
            />
            <Typography
              className="logout-text"
              sx={{
                color: isDark ? "rgba(255, 255, 255, 0.6)" : "#64748B",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              Đăng xuất
            </Typography>
          </Box>

          {/* Version */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 12px 0",
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                color: isDark ? "rgba(255, 255, 255, 0.4)" : "#9CA3AF",
                letterSpacing: "0.05em",
                fontWeight: "600",
              }}
            >
              {appVersion}
            </Typography>
          </Box>
        </Box>
      </Drawer>
      <Main open={open} isMobile={isMobile}>
        {children}
      </Main>
    </Box>
  );
}
